import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Download, Loader2, RotateCcw, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { api } from '@/lib/api/client';
import { useQuery } from '@tanstack/react-query';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface VoiceBaseRow {
  baseId?: number;
  baseName?: string;
  userId?: number;
  username?: string;
  totalNumbers?: string;
  skippedCount?: number;
  baseUrl?: string;
  uploadDate?: string;
  campaignIds?: string;
}

const fmt = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export function ViewVoiceBasePage() {
  const userId = useAuthStore((s) => s.userId);
  const today = new Date();
  const [startDate, setStartDate] = useState<Date>(today);
  const [endDate, setEndDate] = useState<Date>(today);
  const [searchKey, setSearchKey] = useState(0);
  const [campaignId, setCampaignId] = useState('');
  const [appliedCampaignId, setAppliedCampaignId] = useState('');

  const query = useQuery({
    queryKey: ['voice-base', fmt(startDate), fmt(endDate), appliedCampaignId, searchKey],
    queryFn: async () => {
      const res = await api.post<VoiceBaseRow[]>('/api/obd/get/voice/base', {
        startDate: fmt(startDate),
        endDate: fmt(endDate),
        userId: Number(userId),
        ...(appliedCampaignId ? { campaignIds: appliedCampaignId } : {}),
      });
      return Array.isArray(res) ? res : [];
    },
    staleTime: 30_000,
  });

  const rows = query.data ?? [];

  const columns = useMemo<ColumnDef<VoiceBaseRow>[]>(
    () => [
      {
        accessorKey: 'baseId',
        header: 'Base ID',
        size: 90,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.baseId}
          </span>
        ),
      },
      { accessorKey: 'baseName', header: 'Base Name' },
      {
        accessorKey: 'campaignIds',
        header: 'Campaign ID',
        size: 140,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.campaignIds || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'userId',
        header: 'User ID',
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.userId}
          </span>
        ),
      },
      { accessorKey: 'username', header: 'Username' },
      {
        id: 'uploadedCount',
        header: 'Uploaded',
        size: 120,
        cell: ({ row }) => {
          const valid = Number(row.original.totalNumbers ?? 0);
          const skipped = row.original.skippedCount ?? 0;
          return (
            <Badge variant="secondary">{(valid + skipped).toLocaleString()}</Badge>
          );
        },
      },
      {
        accessorKey: 'totalNumbers',
        header: 'Valid',
        size: 100,
        cell: ({ row }) => (
          <span className="text-sm">{Number(row.original.totalNumbers ?? 0).toLocaleString()}</span>
        ),
      },
      {
        id: 'skippedCount',
        header: 'Skipped',
        size: 100,
        cell: ({ row }) => {
          const skipped = row.original.skippedCount ?? 0;
          if (skipped === 0) return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              {skipped.toLocaleString()}
            </Badge>
          );
        },
      },
      { accessorKey: 'uploadDate', header: 'Upload Date', size: 180 },
      {
        id: 'actions',
        header: 'Action',
        size: 100,
        cell: ({ row }) => {
          const url = row.original.baseUrl;
          if (!url) return '—';
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
            >
              <Download className="h-3.5 w-3.5" />
              Download
            </Button>
          );
        },
      },
    ],
    [],
  );

  const handleSearch = () => {
    setAppliedCampaignId(campaignId.trim());
    setSearchKey((k) => k + 1);
  };
  const handleReset = () => {
    const t = new Date();
    setStartDate(t);
    setEndDate(t);
    setCampaignId('');
    setAppliedCampaignId('');
    setSearchKey((k) => k + 1);
  };

  const canView = usePermission('Manage Campaign.View Voice Base', BASE_PERMISSION.VIEW);

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Voice Base Files"
        description="Uploaded contact lists (base files) for voice campaigns."
      />
      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>Search base files by upload date range or campaign ID.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_auto_auto] sm:items-end">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Start date</Label>
              <DatePicker value={startDate} onChange={(d) => d && setStartDate(d)} toDate={new Date()} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-date">End date</Label>
              <DatePicker value={endDate} onChange={(d) => d && setEndDate(d)} fromDate={startDate} toDate={new Date()} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="campaign-id">Campaign ID</Label>
              <Input
                id="campaign-id"
                inputMode="numeric"
                placeholder="Optional"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button onClick={handleSearch} disabled={query.isFetching}>
              {query.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Search
            </Button>
            <Button variant="outline" onClick={handleReset} disabled={query.isFetching}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
      <DataTable<VoiceBaseRow, unknown>
        tableId="voice-base:v1"
        columns={columns}
        data={rows}
        isLoading={query.isFetching}
        searchPlaceholder="Search by name, username..."
        emptyTitle="No base files"
        emptyDescription="No base files found for the selected date range."
        getRowId={(row) => String(row.baseId ?? Math.random())}
        exportFilename={`voice-base-${fmt(new Date())}.csv`}
        pageSize={25}
      />
    </FillPage>
  );
}